import React, { useState } from 'react';
import {
  Box, Typography, Paper, IconButton, Tooltip, Snackbar, Alert,
  Chip, useTheme, useMediaQuery,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import MicNoneRoundedIcon from '@mui/icons-material/MicNoneRounded';
import MedicalServicesRoundedIcon from '@mui/icons-material/MedicalServicesRounded';
import HealingRoundedIcon from '@mui/icons-material/HealingRounded';
import FavoriteRoundedIcon from '@mui/icons-material/FavoriteRounded';
import ChildCareRoundedIcon from '@mui/icons-material/ChildCareRounded';
import RemoveRedEyeRoundedIcon from '@mui/icons-material/RemoveRedEyeRounded';
import AccessibilityNewRoundedIcon from '@mui/icons-material/AccessibilityNewRounded';
import TimerRoundedIcon from '@mui/icons-material/TimerRounded';

const PLANTILLAS = [
  {
    id: 'medicina-interna',
    nombre: 'Medicina Interna',
    icon: <MedicalServicesRoundedIcon />,
    gradient: 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)',
    color: '#0ea5e9',
    paciente: 'Mujer de 52 años · Primera vez · Cefalea + fatiga + mareo',
    duracionAprox: '~3 min',
    nivel: 'Medio',
    guion: `DOCTOR: Buenos días, soy el doctor X. Pase, siéntese. ¿Es su primera visita aquí?
PACIENTE: Buenos días doctor. Sí, primera vez.
DOCTOR: Cuénteme, ¿qué la trae a la consulta?
PACIENTE: Doctor, llevo como dos meses sintiéndome muy cansada. Y me han dado dolores de cabeza casi todos los días.
DOCTOR: Cuénteme más del dolor de cabeza. ¿Cómo es, dónde le duele, cuándo aparece?
PACIENTE: Es como una presión en la frente y en la nuca. Me empieza en la mañana al despertar y a veces dura todo el día. Lo siento más fuerte cuando estoy estresada.
DOCTOR: ¿Náusea, vómito, sensibilidad a la luz?
PACIENTE: A veces siento mareo cuando me agacho o me levanto rápido. Pero náusea no, ni vómito.
DOCTOR: ¿Y el cansancio cómo es?
PACIENTE: Me canso haciendo cosas que antes no me cansaban. Subir las escaleras de mi casa por ejemplo. Y duermo mal, me despierto varias veces en la noche.
DOCTOR: Le voy a hacer un repaso por aparatos y sistemas. Cardiovascular: ¿dolor en el pecho, palpitaciones, falta de aire?
PACIENTE: Falta de aire cuando me agito, pero pensé que era por el sobrepeso.
DOCTOR: Respiratorio: ¿tos, expectoración, sibilancias?
PACIENTE: No.
DOCTOR: Digestivo: ¿acidez, dolor abdominal, cambios en las evacuaciones?
PACIENTE: Acidez muy seguido, sobre todo en la noche. Las evacuaciones normales.
DOCTOR: Urinario: ¿se levanta a orinar en la noche, sed excesiva, orina con frecuencia?
PACIENTE: Me levanto una o dos veces. Y tomo bastante agua durante el día.
DOCTOR: Genital: ¿cómo está su menstruación?
PACIENTE: Estoy en perimenopausia, mis reglas son irregulares desde hace dos años. La última fue hace tres meses.
DOCTOR: ¿Bochornos, sudoraciones nocturnas, cambios de ánimo?
PACIENTE: Sí, bochornos en la noche, y me he sentido más irritable.
DOCTOR: Neurológico: ¿pérdida de fuerza, hormigueo, problemas de visión, episodios de desmayo?
PACIENTE: Nada de eso.
DOCTOR: Antecedentes familiares. ¿Diabetes, hipertensión, infartos?
PACIENTE: Mi mamá tiene hipertensión y diabetes. Mi papá murió de infarto a los sesenta y cinco años.
DOCTOR: Antecedentes personales. ¿Alguna enfermedad diagnosticada, cirugías previas?
PACIENTE: Nunca me han diagnosticado nada. Solo cesárea hace veinte años.
DOCTOR: ¿Embarazos, abortos?
PACIENTE: Dos embarazos, dos cesáreas. Sin abortos.
DOCTOR: ¿Toma medicamentos, vitaminas, suplementos?
PACIENTE: Solo ibuprofeno cuando me duele la cabeza, casi diario últimamente.
DOCTOR: ¿Alergias a medicamentos?
PACIENTE: No, ninguna.
DOCTOR: ¿Fuma, toma alcohol?
PACIENTE: No fumo. Tomo vino los fines de semana, una o dos copas.
DOCTOR: ¿Ejercicio?
PACIENTE: Casi nada doctor.
DOCTOR: Bien. Vamos a explorarla. Está consciente, orientada, sin compromiso del estado general. Le voy a tomar signos vitales. Tensión arterial brazo derecho ciento cincuenta y ocho sobre noventa y seis, brazo izquierdo ciento cincuenta y cinco sobre noventa y cuatro. Frecuencia cardíaca ochenta y dos por minuto, regular. Frecuencia respiratoria dieciséis. Temperatura treinta y seis cuatro. Saturación de oxígeno noventa y siete por ciento al aire ambiente. Peso ochenta y dos kilos, talla un metro sesenta, índice de masa corporal treinta y dos, obesidad grado uno.
PACIENTE: ¿La presión está alta doctor?
DOCTOR: Sí, voy a registrarlo y ahora le explico. Cabeza normocéfala, conjuntivas no pálidas ni ictéricas. Cuello sin adenopatías, tiroides no palpable. Ruidos cardíacos rítmicos, primero y segundo bien definidos, no se auscultan soplos. Campos pulmonares bien ventilados, sin estertores ni sibilancias. Abdomen globoso, blando, depresible, no doloroso, sin masas ni visceromegalias. No edema en miembros inferiores. Pulsos periféricos presentes y simétricos. Reflejos osteotendinosos normales.
PACIENTE: ¿Qué tengo doctor?
DOCTOR: Mi impresión diagnóstica es: primero, hipertensión arterial estadio uno de novo, es decir, recién detectada. Segundo, probable dislipidemia que vamos a confirmar con laboratorios. Tercero, obesidad grado uno. Cuarto, síndrome climatérico por la perimenopausia. Y quinto, cefalea tensional probablemente relacionada con la presión alta y al uso frecuente de ibuprofeno.
PACIENTE: ¿Y qué vamos a hacer?
DOCTOR: Le voy a pedir laboratorios. Biometría hemática completa, química sanguínea de seis elementos, perfil lipídico completo, hemoglobina glicosilada, examen general de orina, perfil tiroideo con TSH y T4 libre, ácido úrico y creatinina sérica.
PACIENTE: De acuerdo.
DOCTOR: Estudios de gabinete: radiografía de tórax posteroanterior, electrocardiograma de doce derivaciones y ultrasonido de hígado y vías biliares para descartar hígado graso.
PACIENTE: Está bien.
DOCTOR: Interconsulta a ginecología para evaluación del climaterio.
PACIENTE: De acuerdo.
DOCTOR: Recetas. Losartán cincuenta miligramos tabletas, una tableta cada veinticuatro horas por la mañana, treinta tabletas. Si tolera bien, en cuatro semanas evaluamos subir la dosis.
PACIENTE: ¿Es de por vida?
DOCTOR: Muy probablemente sí. Le explico en la consulta de seguimiento. Le voy a pedir suspender el ibuprofeno y usar paracetamol quinientos miligramos cada ocho horas solo si tiene dolor de cabeza, máximo tres días seguidos, quince tabletas.
PACIENTE: ¿Y para la acidez?
DOCTOR: Omeprazol veinte miligramos cápsulas, una cápsula en ayunas treinta minutos antes del desayuno, durante cuatro semanas, treinta cápsulas.
PACIENTE: De acuerdo.
DOCTOR: Indicaciones generales. Dieta DASH, baja en sodio, máximo dos gramos de sal al día, alta en verduras, frutas, granos integrales y pescado. Caminata de treinta minutos cinco días por la semana, iniciando gradual.
PACIENTE: Está bien.
DOCTOR: La cito en cuatro semanas con todos los resultados. Signos de alarma: si la presión le sube de ciento sesenta sobre cien, dolor de cabeza intenso súbito, vómito, visión borrosa, dolor en el pecho o falta de aire en reposo, debe ir a urgencias. ¿Alguna duda?
PACIENTE: Ninguna doctor. Muchas gracias.`,
  },
  {
    id: 'cirugia-estetica',
    nombre: 'Cirugía Estética',
    icon: <HealingRoundedIcon />,
    gradient: 'linear-gradient(135deg, #ec4899 0%, #db2777 100%)',
    color: '#ec4899',
    paciente: 'Mujer de 34 años · Primera vez · Valoración mamoplastia',
    duracionAprox: '~3 min',
    nivel: 'Medio',
    guion: `DOCTOR: Buenos días, soy el doctor X, cirujano plástico. Pase, siéntese. ¿Es su primera consulta aquí?
PACIENTE: Buenos días doctor. Sí, primera vez.
DOCTOR: ¿En qué la puedo ayudar?
PACIENTE: Quiero hacerme una mamoplastia de aumento. Llevo años pensándolo.
DOCTOR: Cuénteme su motivo principal.
PACIENTE: Después de mis dos embarazos perdí mucho volumen. Me siento incómoda con mi cuerpo y eso afecta mi autoestima.
DOCTOR: ¿Cuándo fueron sus embarazos?
PACIENTE: Hace nueve y seis años. Los dos fueron cesáreas.
DOCTOR: ¿Lactancia materna en ambos?
PACIENTE: Sí, los dos por un año.
DOCTOR: ¿Tiene dolor mamario, secreción del pezón, alguna bolita que se haya notado?
PACIENTE: No, nada de eso.
DOCTOR: ¿Se ha hecho mastografía o ultrasonido mamario alguna vez?
PACIENTE: No, nunca.
DOCTOR: ¿Cómo es su menstruación?
PACIENTE: Regular, cada veintiocho días, dura cinco días. La última fue hace dos semanas.
DOCTOR: ¿Usa algún método anticonceptivo?
PACIENTE: Dispositivo intrauterino de cobre desde hace tres años.
DOCTOR: ¿Planea más embarazos?
PACIENTE: No, ya tenemos los dos que queríamos.
DOCTOR: Interrogatorio por aparatos y sistemas. Cardiovascular: ¿dolor en el pecho, palpitaciones, falta de aire?
PACIENTE: Nada doctor.
DOCTOR: Respiratorio: ¿tos crónica, asma, alergias respiratorias?
PACIENTE: No.
DOCTOR: Digestivo: ¿acidez, dolor abdominal, alteraciones del intestino?
PACIENTE: Todo bien.
DOCTOR: Urinario y ginecológico: ¿alguna molestia?
PACIENTE: Ninguna.
DOCTOR: Hematológico: ¿problemas con la coagulación, sangrado abundante en cirugías previas, hematomas que aparecen solos?
PACIENTE: En las cesáreas no tuve sangrado de más. No tengo hematomas raros.
DOCTOR: ¿Antecedente de trombosis venosa profunda en piernas o de embolia pulmonar?
PACIENTE: No.
DOCTOR: Endocrino: ¿problemas de tiroides, diabetes?
PACIENTE: No.
DOCTOR: ¿Familiares con cáncer de mama u ovario?
PACIENTE: No, ninguno cercano.
DOCTOR: ¿Diabetes, hipertensión, otras enfermedades crónicas en familia?
PACIENTE: Mi papá es diabético.
DOCTOR: ¿Usted tiene alguna enfermedad crónica diagnosticada?
PACIENTE: No, ninguna.
DOCTOR: ¿Cirugías previas además de las cesáreas?
PACIENTE: No.
DOCTOR: ¿Alergias a medicamentos?
PACIENTE: Sí, soy alérgica a la penicilina. Me da rash en la piel.
DOCTOR: Importante. ¿Toma medicamentos, vitaminas o suplementos?
PACIENTE: Solo un multivitamínico.
DOCTOR: ¿Fuma?
PACIENTE: No.
DOCTOR: Eso es muy bueno para una cirugía. ¿Toma alcohol?
PACIENTE: Vino ocasional, una copa los fines de semana.
DOCTOR: Bien. Vamos a la exploración. Signos vitales: tensión arterial ciento veintidós sobre setenta y ocho, frecuencia cardíaca setenta y dos por minuto, frecuencia respiratoria quince, temperatura treinta y seis cinco, saturación de oxígeno noventa y nueve por ciento al aire ambiente, peso sesenta y cuatro kilos, talla un metro sesenta y dos, índice de masa corporal veinticuatro punto cuatro, normal.
PACIENTE: De acuerdo.
DOCTOR: Está en buen estado general, consciente, colaboradora. Cabeza y cuello sin adenopatías, tiroides no palpable. Ruidos cardíacos rítmicos, sin soplos. Pulmones bien ventilados, sin agregados. Voy a explorar las mamas. Mamas simétricas, copa B pequeña, base mamaria estrecha, ptosis grado uno bilateral, pezones normo formados sin retracción ni secreción. No palpo nódulos ni masas en ningún cuadrante. Axilas sin adenopatías. Surco submamario bien definido. Distancia esternón pezón derecho dieciocho centímetros, izquierdo dieciocho y medio centímetros. Distancia pezón surco derecho seis centímetros, izquierdo seis centímetros. Piel con buena elasticidad, sin estrías importantes.
PACIENTE: De acuerdo.
DOCTOR: Abdomen blando, depresible, cicatrices de cesáreas tipo Pfannenstiel bien cicatrizadas. No edema en piernas, pulsos periféricos presentes y simétricos. Signo de Homans negativo bilateral.
PACIENTE: ¿Soy candidata doctor?
DOCTOR: Sí, es buena candidata. Mi impresión diagnóstica es hipoplasia mamaria bilateral con ptosis grado uno secundaria a involución postlactancia.
PACIENTE: ¿Qué procedimiento me propone?
DOCTOR: Mamoplastia de aumento con implantes de silicón de gel cohesivo, redondos, perfil moderado plus, de trescientos cincuenta centímetros cúbicos, colocación submuscular dual plane, abordaje submamario.
PACIENTE: Está bien.
DOCTOR: Estudios preoperatorios. Mastografía bilateral con ultrasonido complementario. Biometría hemática completa, química sanguínea, tiempos de coagulación TP y TTP, grupo sanguíneo y RH, examen general de orina, perfil de hepatitis B y C, y prueba para VIH. Electrocardiograma de doce derivaciones y radiografía de tórax posteroanterior.
PACIENTE: De acuerdo.
DOCTOR: Interconsulta a cardiología para valoración preanestésica y a medicina interna por antecedente familiar de diabetes.
PACIENTE: Está bien.
DOCTOR: Recetas para el día de la cirugía. Como es alérgica a penicilina, cefalexina quinientos miligramos cápsulas, una cápsula cada seis horas iniciando seis horas antes de la cirugía, total cuatro días, treinta cápsulas. Ketorolaco diez miligramos tabletas, una tableta cada ocho horas vía oral por dos días, seis tabletas. Paracetamol quinientos miligramos, una tableta cada seis horas por cuatro días según dolor, veinticuatro tabletas. Omeprazol veinte miligramos cápsulas, una cápsula en ayunas por cinco días, cinco cápsulas.
PACIENTE: ¿Algo importante antes de la cirugía?
DOCTOR: Suspender aspirina, ibuprofeno y vitamina E diez días antes. Ayuno absoluto de ocho horas. Baño con jabón antiséptico la noche previa y la mañana de la cirugía. Sin maquillaje, sin esmalte de uñas, sin joyería.
PACIENTE: Perfecto.
DOCTOR: La cito en tres semanas con todos los estudios y la autorización de cardiología. Si tiene fiebre, dolor mamario o algún cambio en las mamas antes de la cita, me avisa de inmediato. ¿Alguna duda?
PACIENTE: Ninguna doctor. Muchas gracias.`,
  },
  {
    id: 'cardiologia',
    nombre: 'Cardiología',
    icon: <FavoriteRoundedIcon />,
    gradient: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
    color: '#ef4444',
    paciente: 'Hombre de 55 años · Primera vez · Palpitaciones recurrentes',
    duracionAprox: '~3 min',
    nivel: 'Alto',
    guion: `DOCTOR: Buenos días, soy el doctor X, cardiólogo. ¿Es su primera consulta aquí?
PACIENTE: Buenos días doctor. Sí, primera vez. Me mandó mi médico general.
DOCTOR: ¿Qué le dijo su médico?
PACIENTE: Que tengo arritmias y necesito que me vea un cardiólogo.
DOCTOR: Cuénteme lo que ha sentido.
PACIENTE: Doctor, llevo como tres meses con episodios en que siento que el corazón me late muy rápido y muy fuerte, como descontrolado.
DOCTOR: ¿Cuántos episodios ha tenido?
PACIENTE: Como seis o siete. El último fue hace tres días.
DOCTOR: ¿Cuánto le duran?
PACIENTE: Algunos quince o veinte minutos, pero el último me duró casi dos horas.
DOCTOR: ¿Cómo aparecen? ¿Con algo que esté haciendo, en reposo, en la noche?
PACIENTE: A veces en reposo viendo televisión. Otras después de tomar café. Y uno fue cuando estaba enojado en el trabajo.
DOCTOR: ¿Cómo se sienten? Trate de describírmelo.
PACIENTE: Como un aleteo en el pecho. Irregular. Y me dan ganas de toser. A veces me mareo.
DOCTOR: ¿Ha perdido el conocimiento o sentido que se va a desmayar?
PACIENTE: Una vez sentí que me iba a desmayar, me senté rápido y se me pasó.
DOCTOR: ¿Dolor en el pecho, falta de aire?
PACIENTE: Falta de aire durante los episodios. Dolor no, solo opresión.
DOCTOR: ¿Suda durante los episodios?
PACIENTE: Sí, sudoración fría.
DOCTOR: Interrogatorio por sistemas. Respiratorio: ¿tos, expectoración, asma?
PACIENTE: No.
DOCTOR: Digestivo: ¿acidez, dolor abdominal, alteraciones del intestino?
PACIENTE: Acidez frecuente desde hace un año.
DOCTOR: ¿Ha bajado o subido de peso sin querer?
PACIENTE: Subí cinco kilos en el último año.
DOCTOR: Urinario: ¿se levanta a orinar en la noche?
PACIENTE: Una vez.
DOCTOR: ¿Cómo está el sueño?
PACIENTE: Mi esposa dice que ronco mucho y que dejo de respirar a ratos. Me despierto cansado.
DOCTOR: Eso es importante. ¿Cefalea matutina, somnolencia diurna?
PACIENTE: Sí, ambas cosas. En el trabajo me cabeceo después del almuerzo.
DOCTOR: Neurológico: ¿debilidad, hormigueo, problemas de visión?
PACIENTE: Nada.
DOCTOR: Antecedentes personales. ¿Diabetes, hipertensión, colesterol alto?
PACIENTE: Me dijeron hace cinco años que tenía presión alta, pero no he tomado nada porque no me sentía mal.
DOCTOR: ¿Algún infarto, derrame cerebral, hospitalizaciones?
PACIENTE: No.
DOCTOR: ¿Cirugías previas?
PACIENTE: Apendicectomía a los treinta años.
DOCTOR: Antecedentes familiares. ¿Problemas del corazón o muerte súbita en la familia?
PACIENTE: Mi papá tuvo infarto a los sesenta años. Mi tío tuvo arritmias.
DOCTOR: ¿Fuma?
PACIENTE: Fumé veinte años, ya lo dejé hace tres.
DOCTOR: ¿Alcohol?
PACIENTE: Los fines de semana, dos o tres cervezas, a veces vino.
DOCTOR: ¿Café?
PACIENTE: Tres tazas al día.
DOCTOR: ¿Medicamentos, suplementos?
PACIENTE: Nada.
DOCTOR: ¿Alergias?
PACIENTE: A las sulfas. Me dio sarpullido en la adolescencia.
DOCTOR: Bien. Vamos a la exploración. Signos vitales: tensión arterial brazo derecho ciento cuarenta y ocho sobre noventa y dos, brazo izquierdo ciento cuarenta y cinco sobre noventa. Frecuencia cardíaca setenta y ocho por minuto, irregular ahora mismo. Frecuencia respiratoria diecisiete. Temperatura treinta y seis cinco. Saturación de oxígeno noventa y siete por ciento al aire ambiente. Peso noventa y cinco kilos, talla un metro setenta y cinco, índice de masa corporal treinta y uno, obesidad grado uno. Circunferencia de cuello cuarenta y dos centímetros.
PACIENTE: ¿Está alterado el corazón ahora?
DOCTOR: Su pulso está irregular en este momento. Cabeza y cuello sin adenopatías, tiroides no palpable, no soplos carotídeos. Ruidos cardíacos arrítmicos, no se distingue claramente segundo ruido en cada latido. No se auscultan soplos definidos. Yugulares no ingurgitadas a treinta grados. Pulmones bien ventilados, sin estertores ni sibilancias. Abdomen blando, depresible, hígado no palpable. Pulsos periféricos presentes pero irregulares. Sin edema en miembros inferiores. Mallampati clase tres en orofaringe.
PACIENTE: ¿Qué tengo doctor?
DOCTOR: Mi impresión diagnóstica es muy probable fibrilación auricular paroxística por la irregularidad del pulso ahora mismo. Tiene también hipertensión arterial sin tratamiento, obesidad grado uno y sospecha alta de apnea obstructiva del sueño.
PACIENTE: ¿Es grave?
DOCTOR: La fibrilación auricular aumenta el riesgo de embolia cerebral si no se controla. Por eso vamos a actuar rápido.
PACIENTE: Está bien.
DOCTOR: Estudios. Vamos a hacerle ahora mismo un electrocardiograma de doce derivaciones para confirmar el ritmo. Adicionalmente le voy a pedir Holter de veinticuatro horas, ecocardiograma transtorácico, perfil tiroideo con TSH y T4 libre, biometría hemática, química sanguínea de seis elementos, perfil lipídico completo, hemoglobina glicosilada, electrolitos séricos, urea y creatinina.
PACIENTE: De acuerdo.
DOCTOR: Interconsulta a neumología para polisomnografía por sospecha de apnea obstructiva del sueño. Interconsulta a nutrición para plan de reducción de peso.
PACIENTE: Está bien.
DOCTOR: Recetas. Bisoprolol cinco miligramos tabletas, una tableta cada veinticuatro horas en la mañana, treinta tabletas. Atorvastatina cuarenta miligramos tabletas, una tableta en la noche, treinta tabletas, pendiente ajustar con perfil lipídico. Aspirina cien miligramos tabletas, una tableta con el almuerzo, treinta tabletas, mientras confirmamos diagnóstico antes de decidir anticoagulación oral. Omeprazol veinte miligramos cápsulas, una cápsula en ayunas por seis semanas, cuarenta y dos cápsulas.
PACIENTE: ¿Y para evitar la embolia?
DOCTOR: Su puntuación CHA2DS2-VASc da dos puntos por edad e hipertensión. Por ahora prefiero confirmar el diagnóstico con el Holter antes de iniciar anticoagulación con apixabán o rivaroxabán. La aspirina es una medida intermedia mientras tanto.
PACIENTE: De acuerdo.
DOCTOR: Indicaciones generales. Suspender el café por ahora. Reducir alcohol a máximo dos copas por semana. Nada de bebidas energéticas. Dieta baja en sodio.
PACIENTE: Sí doctor.
DOCTOR: Lo cito en dos semanas con resultados del Holter y los laboratorios. Signos de alarma: si tiene un episodio de palpitaciones que dure más de treinta minutos, dolor en el pecho, falta de aire intensa, mareo persistente, pérdida del conocimiento, o sospecha de embolia como debilidad de un lado del cuerpo o dificultad para hablar, va de inmediato a urgencias.
PACIENTE: Entendido doctor. Muchas gracias.`,
  },
  {
    id: 'pediatria',
    nombre: 'Pediatría',
    icon: <ChildCareRoundedIcon />,
    gradient: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
    color: '#f59e0b',
    paciente: 'Niño de 4 años · Primera vez · Tos crónica + sibilancias',
    duracionAprox: '~3 min',
    nivel: 'Medio',
    guion: `DOCTOR: Buenos días, soy el doctor X, pediatra. ¿Cómo se llaman?
MADRE: Buenos días doctor. Soy Carmen y este es mi hijo Mateo, tiene cuatro años. Es la primera vez que venimos.
DOCTOR: Hola Mateo. ¿Qué le pasa al campeón?
MADRE: Doctor, lleva como cuatro meses con tos que no se le quita. Y a veces se le oye un silbido en el pecho.
DOCTOR: Cuénteme más. ¿Cuándo tose más, en el día o en la noche?
MADRE: Sobre todo en la noche. A veces se despierta con tos seca. Y también cuando juega mucho o corre, se le agita.
DOCTOR: ¿Qué tipo de tos? ¿Seca o con flema?
MADRE: La mayoría del tiempo seca. A veces saca un poco de flema clarita.
DOCTOR: ¿Ha tenido fiebre con estos episodios?
MADRE: No, no es por gripa.
DOCTOR: ¿Empeora con algo en particular? ¿Animales, polvo, frío, ejercicio?
MADRE: Sí, cuando juega con el perro de mi hermana le da más. Y cuando hace frío también.
DOCTOR: ¿Se ha despertado por falta de aire?
MADRE: Sí, dos o tres veces ha despertado angustiado, como ahogándose.
DOCTOR: Interrogatorio por aparatos. Respiratorio alto: ¿estornudos, nariz tapada, mocos?
MADRE: Siempre tiene la nariz tapada, sobre todo en la mañana. Y estornuda mucho.
DOCTOR: ¿Se rasca los ojos, le pican?
MADRE: Sí, frecuentemente.
DOCTOR: Piel: ¿ha tenido eccema, dermatitis?
MADRE: Cuando era bebé tuvo dermatitis atópica en los cachetes y atrás de las rodillas.
DOCTOR: Digestivo: ¿apetito, evacuaciones, vómito?
MADRE: Come normal, una evacuación al día normal, no vómito.
DOCTOR: Urinario: ¿orina normal, se moja en la noche?
MADRE: Todo normal.
DOCTOR: ¿Cómo va con el crecimiento? ¿Sube de peso adecuado?
MADRE: Bien, en la curva normal según el pediatra anterior.
DOCTOR: ¿Cómo va con el sueño y la actividad general?
MADRE: El sueño se interrumpe por la tos algunas noches. Activo normalmente.
DOCTOR: Antecedentes perinatales. ¿Cómo fue el embarazo y el parto?
MADRE: Embarazo controlado sin complicaciones. Cesárea programada a las treinta y ocho semanas, peso dos kilos novecientos al nacer, sin sufrimiento fetal.
DOCTOR: ¿Lactancia materna?
MADRE: Cuatro meses de pecho, luego fórmula.
DOCTOR: ¿Vacunas al día?
MADRE: Sí, completas según el esquema nacional.
DOCTOR: ¿Hospitalizaciones, cirugías?
MADRE: Una hospitalización a los dos años por bronquiolitis durante tres días.
DOCTOR: Esa es información muy importante. ¿Antecedentes familiares de asma, alergias, atopia?
MADRE: Yo tengo rinitis alérgica. Mi esposo tiene asma desde niño.
DOCTOR: ¿Quién más vive en casa? ¿Hay mascotas, alguien fuma?
MADRE: Vivimos los tres. Nadie fuma. No tenemos mascotas, pero vamos seguido a casa de mi hermana que tiene perro.
DOCTOR: ¿Alergias conocidas a medicamentos o alimentos?
MADRE: No, ninguna.
DOCTOR: ¿Qué le ha dado para la tos?
MADRE: Jarabes de la farmacia pero no le han hecho efecto. Y una vez le dieron ambroxol.
DOCTOR: Bien. Vamos a explorar a Mateo. A ver campeón, déjame revisarte. Signos vitales: frecuencia cardíaca ciento dos por minuto, frecuencia respiratoria veintiséis por minuto, temperatura treinta y seis seis, saturación de oxígeno noventa y seis por ciento al aire ambiente. Peso dieciséis kilos, talla un metro tres centímetros, percentil cincuenta para peso, cincuenta y cinco para talla.
MADRE: Está creciendo bien.
DOCTOR: Sí, perfectamente. Está alerta, reactivo, en buen estado general. Cabeza normocéfala. Conjuntivas con leve hiperemia, ojeras alérgicas marcadas. Mucosa nasal pálida con edema y secreción acuosa, lo que llamamos rinitis alérgica clásica. Orofaringe sin alteraciones, mucosa empedrada en pared posterior. Otoscopia bilateral normal. Cuello sin adenopatías.
MADRE: De acuerdo.
DOCTOR: Ruidos cardíacos rítmicos sin soplos. En tórax escucho disminución leve del murmullo vesicular en ambas bases y sibilancias espiratorias finas bilaterales al hacer espiración forzada. Tiraje intercostal leve. Abdomen blando, no doloroso, sin visceromegalias. Piel con xerosis leve en pliegues de codos y rodillas. Llenado capilar inmediato. Reflejos normales.
MADRE: ¿Qué tiene mi hijo?
DOCTOR: Mi impresión diagnóstica es muy probable asma bronquial leve persistente, rinitis alérgica concomitante, y antecedente de dermatitis atópica. Es lo que llamamos marcha atópica, y con los antecedentes familiares cuadra perfectamente.
MADRE: ¿Es grave?
DOCTOR: Es una enfermedad crónica pero muy controlable con el tratamiento adecuado. La idea es que Mateo lleve una vida normal, sin tos, sin despertarse, jugando como cualquier niño.
MADRE: De acuerdo doctor.
DOCTOR: Estudios. Biometría hemática completa con cuenta diferencial y eosinófilos. Inmunoglobulina E total sérica. Panel de alérgenos básicos por prueba sanguínea para ácaros, polvo, epitelio de perro y polen. Radiografía de tórax posteroanterior y lateral para descartar otra patología pulmonar.
MADRE: Está bien.
DOCTOR: Interconsulta a alergología pediátrica para confirmar perfil alérgico y considerar inmunoterapia a futuro.
MADRE: De acuerdo.
DOCTOR: Recetas. Salbutamol inhalador en aerosol, cien microgramos por puff, dos puffs con espaciador con mascarilla en caso de tos, sibilancia o dificultad para respirar, máximo cada seis horas. Se lo voy a enseñar a usar antes de irse.
MADRE: Perfecto.
DOCTOR: Como controlador de fondo, fluticasona inhalada cincuenta microgramos por puff, dos puffs cada doce horas con espaciador, todos los días sin falta aunque esté bien. Esto es para controlar la inflamación crónica.
MADRE: ¿Por cuánto tiempo?
DOCTOR: Mínimo tres meses, luego evaluamos. Es importante el enjuague de boca después de cada uso para prevenir candidiasis oral.
MADRE: Está bien.
DOCTOR: Para la rinitis alérgica, loratadina jarabe cinco miligramos por cinco mililitros, una cucharadita cada veinticuatro horas en la noche por treinta días.
MADRE: De acuerdo.
DOCTOR: Medidas ambientales muy importantes. Funda antiácaros en almohada y colchón. Lavar sábanas con agua caliente cada semana. Aspirar la casa con frecuencia. Evitar peluches en la cama. Si va a casa de su hermana con perro, llevar el inhalador de rescate.
MADRE: De acuerdo.
DOCTOR: Cita de control en cuatro semanas con todos los resultados. Signos de alarma: si el inhalador de rescate no le funciona, si tiene dificultad para respirar evidente con hundimiento de costillas, si los labios se ven morados, si no puede hablar oraciones completas o si la saturación baja de noventa y dos por ciento, lo lleva inmediatamente a urgencias.
MADRE: Muchas gracias doctor. ¿Me enseña ahora el inhalador?
DOCTOR: Por supuesto, vamos a hacerlo paso a paso.`,
  },
  {
    id: 'oftalmologia',
    nombre: 'Oftalmología',
    icon: <RemoveRedEyeRoundedIcon />,
    gradient: 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)',
    color: '#8b5cf6',
    paciente: 'Mujer de 28 años · Primera vez · Ojo rojo + secreción',
    duracionAprox: '~3 min',
    nivel: 'Medio',
    guion: `DOCTOR: Buenos días, soy la doctora X, oftalmóloga. ¿Es su primera consulta aquí?
PACIENTE: Buenos días doctora. Sí, primera vez.
DOCTOR: Cuénteme, ¿qué la trae?
PACIENTE: Doctora, tengo el ojo derecho rojo desde hace tres días.
DOCTOR: Cuénteme cómo empezó.
PACIENTE: Empecé sintiendo como una arenita en el ojo derecho. Al día siguiente amaneció rojo y con lagaña amarilla. La lagaña me pega los párpados en la mañana.
DOCTOR: ¿Cuánta lagaña?
PACIENTE: Bastante. Sobre todo al despertar. En el día también pero menos.
DOCTOR: ¿De qué color?
PACIENTE: Amarillenta, casi como pus.
DOCTOR: ¿Ha tenido dolor profundo en el ojo, no solo molestia superficial?
PACIENTE: Dolor profundo no. Es más como ardor y la sensación de cuerpo extraño.
DOCTOR: ¿Ve borroso, ve halos alrededor de las luces, ha disminuido su visión?
PACIENTE: No, veo igual que siempre.
DOCTOR: ¿Le molesta la luz?
PACIENTE: Un poquito pero no demasiado.
DOCTOR: ¿Y el ojo izquierdo?
PACIENTE: Está bien, sin nada.
DOCTOR: ¿Alguien cerca tuvo lo mismo?
PACIENTE: Sí, mi hijo de cinco años tuvo igual hace una semana, ya está bien.
DOCTOR: ¿Usa lentes o lentes de contacto?
PACIENTE: Uso lentes de contacto blandos diarios, soy miope. Tres dioptrías cada ojo. Los suspendí desde que empezó esto.
DOCTOR: Hizo bien. ¿Usa maquillaje en los ojos?
PACIENTE: Sí, todos los días.
DOCTOR: ¿Lo suspendió al iniciar el cuadro?
PACIENTE: Sí.
DOCTOR: Interrogatorio por aparatos. Respiratorio: ¿catarro, congestión, dolor de garganta, fiebre?
PACIENTE: No.
DOCTOR: ¿Alergias, asma, rinitis?
PACIENTE: Ninguna.
DOCTOR: ¿Antecedente de herpes labial o genital?
PACIENTE: No.
DOCTOR: Endocrino: ¿diabetes, enfermedades tiroideas?
PACIENTE: No.
DOCTOR: Reumatológico: ¿artritis, lupus, enfermedades autoinmunes?
PACIENTE: No.
DOCTOR: Antecedentes oftalmológicos. ¿Cirugías oculares previas, traumatismos en los ojos?
PACIENTE: No, ninguno.
DOCTOR: ¿Antecedentes familiares oftalmológicos? ¿Glaucoma, retinopatía, ceguera?
PACIENTE: Mi mamá usa lentes pero nada más importante.
DOCTOR: ¿Embarazo o lactancia actual?
PACIENTE: No.
DOCTOR: ¿Alergias a medicamentos?
PACIENTE: Ninguna conocida.
DOCTOR: ¿Toma medicamentos?
PACIENTE: Solo anticonceptivos orales.
DOCTOR: Bien. Vamos a la exploración. Signos vitales: tensión arterial ciento dieciocho sobre setenta y seis, frecuencia cardíaca setenta y cuatro, temperatura treinta y seis cuatro, frecuencia respiratoria quince, saturación de oxígeno noventa y nueve por ciento, peso cincuenta y ocho kilos, talla un metro sesenta y cinco. Buen estado general, sin compromiso sistémico.
PACIENTE: De acuerdo.
DOCTOR: Exploración oftalmológica. Agudeza visual sin corrección ojo derecho veinte cien, ojo izquierdo veinte cien. Con corrección habitual ojo derecho veinte veinticinco, ojo izquierdo veinte veinte. Presión intraocular por tonometría de aplanación: ojo derecho catorce milímetros de mercurio, ojo izquierdo trece milímetros de mercurio, ambas normales. Motilidad ocular conservada en todas las posiciones, sin diplopía.
PACIENTE: Está bien.
DOCTOR: Inspección externa. Párpados sin tumefacción ni eritema marcado. Pestañas sin colaretes ni descamación. En el ojo derecho veo hiperemia conjuntival difusa, predominio en fórnix y conjuntiva tarsal, con folículos en conjuntiva tarsal inferior. Secreción mucopurulenta moderada en saco conjuntival inferior. Quemosis leve. Sin reacción ciliar.
PACIENTE: De acuerdo.
DOCTOR: Córnea derecha transparente, sin defectos epiteliales. Hago tinción con fluoresceína: no se ven áreas de captación. Cámara anterior bien formada, sin células ni flare. Iris íntegro. Pupila isocórica de tres milímetros, reactiva. Reflejos pupilares conservados directo y consensual. Cristalino transparente.
PACIENTE: Está bien.
DOCTOR: Fondo de ojo con dilatación: papila óptica de bordes nítidos, excavación cero punto tres, vasos calibre normal, relación arteria vena dos tercios, mácula sin alteraciones, brillo foveal conservado, periferia retiniana sin desgarros. Ojo izquierdo completamente normal en todos los aspectos.
PACIENTE: De acuerdo.
DOCTOR: Palpo ganglio preauricular derecho de unos tres milímetros, no doloroso. Resto sin adenopatías.
PACIENTE: ¿Qué tengo doctora?
DOCTOR: Mi impresión diagnóstica es conjuntivitis aguda bacteriana del ojo derecho, miopía bilateral conocida de tres dioptrías. La probable causa es contagio de su hijo y favorecido por el uso de lentes de contacto.
PACIENTE: ¿Necesito estudios?
DOCTOR: Por ahora no son necesarios estudios. El cuadro es típico y la exploración es contundente. Si en cinco a siete días no mejora, sí pediremos cultivo conjuntival con antibiograma y tinción de Gram.
PACIENTE: Está bien.
DOCTOR: Recetas. Moxifloxacino oftálmico cero punto cinco por ciento solución, gotero de cinco mililitros, instilar una gota en ojo derecho cada cuatro horas durante siete días. Total un gotero.
PACIENTE: De acuerdo.
DOCTOR: Hipromelosa cero punto cinco por ciento, lágrimas artificiales sin conservadores, instilar una gota en ojo derecho cada cuatro horas alternando con el antibiótico, por dos semanas. Total dos goteros.
PACIENTE: Está bien.
DOCTOR: Indicaciones generales. Suspender lentes de contacto hasta resolución completa, y desechar los que tiene en uso, comprar nuevos cuando esté curada. Desinfectar o cambiar el estuche. Suspender maquillaje en ojos durante el tratamiento y desechar el actual. Compresas tibias sobre el ojo afectado, tres veces al día por cinco minutos. Higiene estricta de manos, toalla y almohada individual para evitar contagio familiar.
PACIENTE: De acuerdo.
DOCTOR: La cito en siete días para revisar resolución. Signos de alarma: si presenta dolor profundo intenso, disminución de la visión, fotofobia importante, opacidad corneal visible o si el cuadro empeora a pesar del tratamiento, acude antes a consulta o a urgencias oftalmológicas.
PACIENTE: Muchas gracias doctora.`,
  },
  {
    id: 'traumatologia',
    nombre: 'Traumatología y Ortopedia',
    icon: <AccessibilityNewRoundedIcon />,
    gradient: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
    color: '#10b981',
    paciente: 'Hombre de 22 años · Primera vez · Esguince agudo de tobillo',
    duracionAprox: '~3 min',
    nivel: 'Medio',
    guion: `DOCTOR: Buenos días, soy el doctor X, traumatólogo. ¿Es su primera consulta aquí?
PACIENTE: Buenos días doctor. Sí.
DOCTOR: Pase, siéntese. ¿Qué le pasó?
PACIENTE: Ayer jugando fútbol caí mal de un salto y me torcí el tobillo derecho.
DOCTOR: Cuénteme exactamente cómo ocurrió.
PACIENTE: Salté para cabecear un balón y al caer puse el pie de lado, hacia adentro. Sentí un crujido y caí.
DOCTOR: ¿Pudo seguir jugando?
PACIENTE: No, me dolió tanto que no pude apoyarme. Mis compañeros me sacaron del campo.
DOCTOR: ¿Cómo es el dolor ahora?
PACIENTE: Sigue siendo fuerte, como un siete sobre diez. Aumenta cuando intento apoyar el pie.
DOCTOR: ¿Dónde le duele exactamente?
PACIENTE: Aquí en la parte de afuera del tobillo. Por debajo del hueso.
DOCTOR: ¿Se inflamó?
PACIENTE: Sí, mucho. Y se me puso morado.
DOCTOR: ¿Qué hizo en casa?
PACIENTE: Me puse hielo veinte minutos cada hora, un vendaje compresivo y mantuve el pie en alto.
DOCTOR: Muy bien hecho. ¿Tomó algún medicamento?
PACIENTE: Ibuprofeno cuatrocientos, dos tabletas. Me ayudó un poco.
DOCTOR: ¿Ha podido apoyar algo el pie?
PACIENTE: Solo la punta. Si trato de apoyar todo el pie es muy doloroso.
DOCTOR: ¿Siente que el tobillo le falla, como que se le va?
PACIENTE: Sí, sensación de inestabilidad.
DOCTOR: ¿Adormecimiento, hormigueo o sensación rara en los dedos del pie?
PACIENTE: No.
DOCTOR: ¿Hace cuánto juega fútbol? ¿Ha tenido lesiones similares antes?
PACIENTE: Llevo siete años jugando, tres veces por semana. Nunca había tenido un esguince así.
DOCTOR: ¿Otros traumatismos previos en ese mismo tobillo o en el otro?
PACIENTE: No, nunca.
DOCTOR: Interrogatorio por aparatos. Cardiovascular: ¿dolor en el pecho, palpitaciones?
PACIENTE: Nada.
DOCTOR: Respiratorio: ¿tos, falta de aire?
PACIENTE: No.
DOCTOR: Digestivo: ¿acidez, gastritis, antecedente de úlcera?
PACIENTE: No, todo bien.
DOCTOR: Antecedentes personales. ¿Enfermedades crónicas? ¿Diabetes, hipertensión, problemas de coagulación?
PACIENTE: Ninguna.
DOCTOR: ¿Cirugías previas?
PACIENTE: Apendicectomía a los catorce años.
DOCTOR: ¿Alergias a medicamentos?
PACIENTE: A la dipirona. Me dio rash.
DOCTOR: Importante. ¿Toma medicamentos habitualmente?
PACIENTE: No, soy sano.
DOCTOR: ¿Fuma, alcohol?
PACIENTE: No fumo. Alcohol los fines de semana, dos o tres cervezas.
DOCTOR: ¿Antecedentes familiares importantes? ¿Osteoporosis, problemas óseos?
PACIENTE: Ninguno conocido.
DOCTOR: ¿Vacuna antitetánica al día?
PACIENTE: No estoy seguro. La última fue hace años en la escuela.
DOCTOR: La revisamos al final. Vamos a explorar. Signos vitales: tensión arterial ciento veinticinco sobre setenta y ocho, frecuencia cardíaca ochenta y cuatro por minuto, frecuencia respiratoria dieciséis, temperatura treinta y seis cinco, saturación de oxígeno noventa y nueve por ciento, peso setenta y cinco kilos, talla un metro setenta y ocho, índice de masa corporal veintitrés punto siete, normal.
PACIENTE: Bien.
DOCTOR: Estado general bueno, consciente, cooperador, marcha antiálgica claudicante. Ahora el tobillo. Inspección: aumento de volumen evidente en cara lateral del tobillo derecho. Equimosis violácea que se extiende desde maléolo lateral hacia el dorso del pie. Piel íntegra, sin heridas, sin deformidades evidentes. Temperatura local aumentada.
PACIENTE: De acuerdo.
DOCTOR: Palpación. Dolor selectivo importante en trayecto del ligamento peroneoastragalino anterior, dolor moderado en peroneocalcáneo, mínimo en peroneoastragalino posterior. No dolor en maléolo lateral ni medial, ni en base del quinto metatarsiano, ni en escafoides, lo cual hace menos probable fractura según criterios de Ottawa, pero aun así pediremos radiografía. Pulsos pedio y tibial posterior presentes y simétricos. Sensibilidad conservada en todos los dermatomas del pie. Llenado capilar inmediato. Temperatura distal conservada.
PACIENTE: De acuerdo.
DOCTOR: Maniobras especiales. Maniobra de cajón anterior positiva con dolor importante. Maniobra de inversión forzada positiva. Maniobra de eversión negativa. Squeeze test negativo, lo cual hace menos probable lesión sindesmótica. Rangos de movilidad: flexión dorsal limitada a cinco grados, flexión plantar a quince grados, todos los movimientos con dolor. Rodilla derecha sin signos de derrame, ligamentos colaterales y cruzados conservados, McMurray negativo. Cadera con movilidad completa sin dolor.
PACIENTE: ¿Qué tan grave es?
DOCTOR: Mi impresión diagnóstica es esguince de tobillo derecho grado dos, con lesión parcial del ligamento peroneoastragalino anterior y compromiso del peroneocalcáneo. Pendiente descartar fractura por radiografía.
PACIENTE: De acuerdo.
DOCTOR: Estudios. Radiografía de tobillo derecho proyección anteroposterior, lateral y mortaja. Si la radiografía sale con sospecha o el cuadro no evoluciona bien, pediremos resonancia magnética en dos semanas.
PACIENTE: Está bien.
DOCTOR: Tratamiento. Aplicamos protocolo PRICE modificado. Protección con férula posterior de yeso por dos semanas, luego cambio a tobillera funcional rígida por cuatro semanas más. Reposo deportivo absoluto seis semanas. Hielo veinte minutos cada cuatro horas durante las primeras setenta y dos horas. Compresión con vendaje elástico cuando no tenga la férula. Elevación del miembro por encima del nivel del corazón cuando esté sentado o acostado.
PACIENTE: ¿Voy a poder usar muletas?
DOCTOR: Sí, muletas para descarga total de peso por dos semanas, luego apoyo progresivo según tolerancia.
PACIENTE: De acuerdo.
DOCTOR: Recetas. Ibuprofeno seiscientos miligramos tabletas, una tableta cada ocho horas con alimentos por cinco días, total quince tabletas. Paracetamol quinientos miligramos tabletas, una tableta cada seis horas como rescate si el dolor persiste, por cinco días, total veinte tabletas. Omeprazol veinte miligramos cápsulas, una cápsula en ayunas durante diez días para proteger el estómago del antiinflamatorio, total diez cápsulas.
PACIENTE: Está bien.
DOCTOR: Interconsulta a rehabilitación física para iniciar terapia a las dos semanas con ejercicios de propiocepción, equilibrio y fortalecimiento progresivo de peroneos.
PACIENTE: ¿Y la antitetánica?
DOCTOR: Como no hay herida abierta, por ahora no es urgente. Confirme con su médico general y póngasela si tiene más de diez años sin refuerzo.
PACIENTE: De acuerdo.
DOCTOR: Cita de control en diez días con la radiografía. Reincorporación gradual al deporte de seis a ocho semanas y siempre después de pasar por rehabilitación. Signos de alarma: si presenta dolor que aumenta a pesar del medicamento, hormigueo o adormecimiento que no había antes, cambio de color a azulado en los dedos, fiebre, o pérdida de sensibilidad, acudir a urgencias.
PACIENTE: Muchas gracias doctor.`,
  },
];

// Renderer del diálogo: parsea líneas con prefijo DOCTOR:/PACIENTE:/MADRE: y
// las pinta como turnos diferenciados. Líneas sin prefijo se concatenan al turno previo.
function DialogoView({ texto, colorDoctor = '#1e3a5f' }) {
  const ROL_REGEX = /^(DOCTOR|DOCTORA|PACIENTE|MADRE|PADRE|FAMILIAR):\s*/i;
  const turnos = [];
  let actual = null;
  (texto || '').split('\n').forEach((linea) => {
    const m = linea.match(ROL_REGEX);
    if (m) {
      if (actual) turnos.push(actual);
      actual = { rol: m[1].toUpperCase(), texto: linea.replace(ROL_REGEX, '').trim() };
    } else if (actual && linea.trim()) {
      actual.texto += ' ' + linea.trim();
    }
  });
  if (actual) turnos.push(actual);

  const esDoctor = (rol) => rol === 'DOCTOR' || rol === 'DOCTORA';
  return (
    <>
      {turnos.map((t, i) => {
        const doctor = esDoctor(t.rol);
        return (
          <Box key={i} sx={{
            display: 'flex',
            justifyContent: doctor ? 'flex-start' : 'flex-end',
          }}>
            <Box sx={{
              maxWidth: '88%',
              px: 1.25, py: 0.9,
              borderRadius: 2,
              borderTopLeftRadius: doctor ? 0.5 : 2,
              borderTopRightRadius: doctor ? 2 : 0.5,
              background: doctor ? '#fff' : colorDoctor + '12',
              border: doctor ? '1px solid #e2e8f0' : `1px solid ${colorDoctor}33`,
              userSelect: 'text',
            }}>
              <Typography sx={{
                fontSize: 9.5, fontWeight: 800,
                color: doctor ? colorDoctor : '#475569',
                letterSpacing: 0.5,
                mb: 0.3,
                textTransform: 'uppercase',
              }}>
                {t.rol}
              </Typography>
              <Typography sx={{
                fontSize: 12.5,
                fontFamily: '"Inter", -apple-system, sans-serif',
                color: '#1e293b',
                lineHeight: 1.5,
              }}>
                {t.texto}
              </Typography>
            </Box>
          </Box>
        );
      })}
    </>
  );
}


export default function PlantillasVoz() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [copiedId, setCopiedId] = useState(null);
  const [snackOpen, setSnackOpen] = useState(false);
  const [probadas, setProbadas] = useState(() => {
    try { return JSON.parse(localStorage.getItem('plantillas_voz_probadas') || '[]'); }
    catch { return []; }
  });

  const handleCopy = async (plantilla) => {
    try {
      await navigator.clipboard.writeText(plantilla.guion);
      setCopiedId(plantilla.id);
      setSnackOpen(true);
      setTimeout(() => setCopiedId(null), 2500);
    } catch (_) {}
  };

  const toggleProbada = (id) => {
    setProbadas((prev) => {
      const next = prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id];
      try { localStorage.setItem('plantillas_voz_probadas', JSON.stringify(next)); } catch {}
      return next;
    });
  };

  return (
    <Box sx={{
      minHeight: '100vh',
      backgroundImage: `
        radial-gradient(circle at 20% 50%, rgba(99, 102, 241, 0.10) 0%, transparent 50%),
        radial-gradient(circle at 80% 20%, rgba(168, 85, 247, 0.08) 0%, transparent 40%)
      `,
      pb: 6,
    }}>
      <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 1100, mx: 'auto' }}>
        {/* Hero header */}
        <Box sx={{
          mb: 3,
          p: { xs: 3, md: 4 },
          borderRadius: 4,
          background: 'linear-gradient(135deg, #1e3a5f 0%, #2d5a87 100%)',
          color: '#fff',
          boxShadow: '0 8px 24px rgba(30, 58, 95, 0.25)',
          position: 'relative',
          overflow: 'hidden',
        }}>
          <Box sx={{
            position: 'absolute', top: -40, right: -40,
            width: 180, height: 180, borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(251, 191, 36, 0.18), transparent)',
          }} />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1, position: 'relative' }}>
            <Box sx={{
              width: 48, height: 48,
              borderRadius: 3,
              background: 'linear-gradient(135deg, #fbbf24, #f59e0b)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: '0 4px 12px rgba(251, 191, 36, 0.4)',
            }}>
              <MicNoneRoundedIcon sx={{ color: '#1e3a5f', fontSize: 26 }} />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 800, fontSize: { xs: 20, md: 24 }, letterSpacing: -0.3 }}>
                Plantillas para probar voz
              </Typography>
              <Typography sx={{ opacity: 0.8, fontSize: 13.5, fontWeight: 500 }}>
                6 diálogos realistas entre doctor y paciente para entrenar el modo voz
              </Typography>
            </Box>
          </Box>
          <Typography sx={{
            mt: 1.5, opacity: 0.85, fontSize: 13, lineHeight: 1.55, maxWidth: 720, position: 'relative',
          }}>
            Abre esta página en tu computadora o tablet y lee el diálogo en voz alta junto con otra persona desde NARA Doctor en el celular.
            La IA diariza la conversación (médico↔paciente) y genera la nota SOAP estructurada. Ideal para entrenar al equipo,
            calibrar diarización por especialidad o validar cambios al pipeline.
          </Typography>
        </Box>

        {/* Plantillas */}
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
          gap: 2.5,
        }}>
          {PLANTILLAS.map((p) => {
            const yaProbada = probadas.includes(p.id);
            const wasCopied = copiedId === p.id;
            return (
              <Paper key={p.id} elevation={0} sx={{
                p: 0,
                borderRadius: 4,
                overflow: 'hidden',
                background: '#fff',
                border: '1px solid rgba(226, 232, 240, 0.85)',
                boxShadow: '0 4px 16px rgba(0, 0, 0, 0.05)',
                position: 'relative',
                transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  boxShadow: '0 12px 32px rgba(0, 0, 0, 0.10)',
                  transform: 'translateY(-2px)',
                },
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0, left: 0, right: 0,
                  height: 4,
                  background: p.gradient,
                },
              }}>
                {/* Header */}
                <Box sx={{ p: 2.5, pb: 1.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box sx={{
                    width: 44, height: 44,
                    borderRadius: 3,
                    background: p.gradient,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    boxShadow: `0 6px 16px ${p.color}40`,
                    color: '#fff',
                    '& svg': { fontSize: 22 },
                  }}>
                    {p.icon}
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 800, fontSize: 16, color: '#0f172a', letterSpacing: -0.2 }}>
                      {p.nombre}
                    </Typography>
                    <Typography sx={{ fontSize: 12, color: '#64748b', fontWeight: 500 }}>
                      {p.paciente}
                    </Typography>
                  </Box>
                </Box>

                {/* Meta chips */}
                <Box sx={{ px: 2.5, pb: 1, display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                  <Chip
                    size="small"
                    icon={<TimerRoundedIcon sx={{ fontSize: 14 }} />}
                    label={p.duracionAprox}
                    sx={{
                      height: 22, fontSize: 11, fontWeight: 600,
                      backgroundColor: 'rgba(99, 102, 241, 0.10)',
                      color: '#6366f1',
                      border: 'none',
                    }}
                  />
                  <Chip
                    size="small"
                    label={`Nivel ${p.nivel}`}
                    sx={{
                      height: 22, fontSize: 11, fontWeight: 600,
                      backgroundColor: p.color + '18',
                      color: p.color,
                      border: 'none',
                    }}
                  />
                  {yaProbada && (
                    <Chip
                      size="small"
                      icon={<CheckRoundedIcon sx={{ fontSize: 14 }} />}
                      label="Probada"
                      sx={{
                        height: 22, fontSize: 11, fontWeight: 700,
                        backgroundColor: 'rgba(16, 185, 129, 0.12)',
                        color: '#10b981',
                        border: 'none',
                      }}
                    />
                  )}
                </Box>

                {/* Diálogo */}
                <Box sx={{
                  mx: 2.5, mt: 1,
                  p: 1.5,
                  borderRadius: 2.5,
                  background: '#f8fafc',
                  border: '1px solid #e2e8f0',
                  position: 'relative',
                  maxHeight: 320,
                  overflowY: 'auto',
                  display: 'flex', flexDirection: 'column', gap: 0.75,
                }}>
                  <DialogoView texto={p.guion} colorDoctor={p.color} />
                </Box>

                {/* Actions */}
                <Box sx={{ p: 2, pt: 1.5, display: 'flex', gap: 1, alignItems: 'center', justifyContent: 'space-between' }}>
                  <Tooltip title={yaProbada ? 'Quitar de probadas' : 'Marcar como probada'}>
                    <IconButton
                      onClick={() => toggleProbada(p.id)}
                      size="small"
                      sx={{
                        color: yaProbada ? '#10b981' : '#94a3b8',
                        '&:hover': { backgroundColor: 'rgba(16, 185, 129, 0.08)' },
                      }}
                    >
                      <CheckRoundedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Box
                    onClick={() => handleCopy(p)}
                    sx={{
                      px: 2, py: 1,
                      borderRadius: 2,
                      background: wasCopied
                        ? 'linear-gradient(135deg, #10b981, #059669)'
                        : p.gradient,
                      color: '#fff',
                      fontSize: 13,
                      fontWeight: 700,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.75,
                      boxShadow: wasCopied
                        ? '0 4px 12px rgba(16, 185, 129, 0.35)'
                        : `0 4px 12px ${p.color}50`,
                      transition: 'all 0.2s',
                      '&:hover': { transform: 'scale(1.03)' },
                      '&:active': { transform: 'scale(0.97)' },
                    }}
                  >
                    {wasCopied ? <CheckRoundedIcon sx={{ fontSize: 16 }} /> : <ContentCopyIcon sx={{ fontSize: 16 }} />}
                    {wasCopied ? 'Copiado' : 'Copiar diálogo'}
                  </Box>
                </Box>
              </Paper>
            );
          })}
        </Box>

        {/* Footer tip */}
        <Paper elevation={0} sx={{
          mt: 3,
          p: 2.5,
          borderRadius: 3,
          background: 'rgba(251, 191, 36, 0.10)',
          border: '1px solid rgba(251, 191, 36, 0.35)',
        }}>
          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
            <Box sx={{
              width: 32, height: 32, borderRadius: 2,
              background: 'linear-gradient(135deg, #fbbf24, #f59e0b)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0,
            }}>
              <MicNoneRoundedIcon sx={{ color: '#fff', fontSize: 18 }} />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: 13.5, color: '#92400e', mb: 0.5 }}>
                Cómo usar los diálogos
              </Typography>
              <Typography sx={{ fontSize: 12.5, color: '#78350f', lineHeight: 1.55 }}>
                Idealmente lee con otra persona (o tú solo cambiando de tono) las líneas de <strong>DOCTOR</strong> y <strong>PACIENTE</strong> en voz natural.
                No digas los prefijos "doctor" o "paciente" en voz alta — son solo guía visual; la diarización los detecta por la voz.
                Cuando termines, di <strong>"Nara, firmar y cerrar"</strong> para procesar la consulta y abrir la pantalla de revisión.
              </Typography>
            </Box>
          </Box>
        </Paper>
      </Box>

      <Snackbar
        open={snackOpen}
        autoHideDuration={2000}
        onClose={() => setSnackOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" variant="filled" sx={{ borderRadius: 2 }}>
          Guion copiado al portapapeles
        </Alert>
      </Snackbar>
    </Box>
  );
}
